import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useToast, useRole, Modal, ConfirmModal } from '../components/shared';
import { money } from '../components/TicketFinance';

async function call(method, url, body) {
  const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || 'Request failed');
  return j;
}

// + = له (company owes him), − = عليه (he owes the company)
export function Balance({ value, big }) {
  const v = Number(value || 0);
  const color = v > 0 ? 'var(--green)' : v < 0 ? 'var(--red)' : 'var(--text3)';
  return (
    <span style={{ color, fontWeight: 800, fontSize: big ? 30 : 18 }} className="mono">
      {money(Math.abs(v))} <span style={{ fontSize: big ? 15 : 12, fontWeight: 600 }}>{v > 0 ? 'له' : v < 0 ? 'عليه' : ''}</span>
    </span>
  );
}

const TYPE = {
  expense: ['💸', 'مصروف دفعه', 'badge-blue'],
  commission: ['⭐', 'عمولة', 'badge-green'],
  advance: ['💵', 'عهدة', 'badge-red'],
  payout: ['🏦', 'صرف له', 'badge-yellow'],
  return: ['↩', 'رد عهدة', 'badge-gray'],
  adjustment: ['⚖', 'تسوية', 'badge-gray'],
};
const COM_STATUS = { no_invoice: ['مفيش فاتورة', 'badge-gray'], pending: ['مستني الدفع', 'badge-yellow'], earned: ['مستحقة', 'badge-green'], loss: ['خسارة', 'badge-red'] };
const KINDS = {
  advance: ['💵 عهدة (سلفة نقدية)', 'فلوس الشركة إدّتها للمهندس يصرف منها — بتتحسب عليه'],
  payout: ['🏦 صرف للمهندس', 'دفعناله مستحقاته (عمولة / مصاريف دفعها) — بتقلل اللي له'],
  return: ['↩ رد عهدة', 'المهندس رجّع فلوس للشركة — بتقلل اللي عليه'],
  adjustment: ['⚖ تسوية', 'تعديل يدوي: موجب = له، سالب = عليه'],
};

export function Engineers() {
  const [list, setList] = useState(null);
  const toast = useToast();
  const nav = useNavigate();
  useEffect(() => { call('GET', '/api/finance/engineers').then(setList).catch(e => toast(e.message, 'error')); }, [toast]);
  const owed = (list || []).reduce((s, u) => s + Math.max(0, u.balance), 0);
  const held = (list || []).reduce((s, u) => s + Math.max(0, -u.balance), 0);

  return (
    <div className="page" dir="rtl">
      <div className="page-header"><h1 className="page-title">المهندسين <span>_</span></h1></div>
      <div className="flex gap-12 mb-16" style={{ flexWrap: 'wrap' }}>
        <div className="stat-card" style={{ flex: 1, minWidth: 200 }}><div className="stat-label">مستحق للمهندسين (له)</div><div className="stat-value green mono">{money(owed)}</div></div>
        <div className="stat-card" style={{ flex: 1, minWidth: 200 }}><div className="stat-label">عهد مع المهندسين (عليه)</div><div className="stat-value red mono">{money(held)}</div></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
        {!list ? <div className="empty">...</div> : list.map(u => (
          <div key={u.id} className="card" style={{ padding: 16, cursor: 'pointer' }} onClick={() => nav(`/engineers/${u.id}`)}>
            <div className="flex items-center gap-8" style={{ justifyContent: 'space-between' }}>
              <div><div className="fw-600" style={{ fontSize: 16 }}>👷 {u.username}</div><div className="text-xs text-dim">{u.role} · {u.totals.tickets} تيكت</div></div>
              <Balance value={u.balance} />
            </div>
            <div className="text-xs text-muted mt-8" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
              <span>عمولات: <b className="mono">{money(u.totals.commissions)}</b></span>
              <span>مصاريف دفعها: <b className="mono">{money(u.totals.expenses_paid)}</b></span>
              <span>عهد: <b className="mono">{money(u.totals.advances)}</b></span>
              <span>اتصرف له: <b className="mono">{money(u.totals.payouts)}</b></span>
              {u.totals.pending_commission > 0 && <span style={{ gridColumn: '1 / -1' }}>⏳ عمولة مستنية الدفع: <b className="mono">{money(u.totals.pending_commission)}</b></span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EntryModal({ user, onClose, onSaved }) {
  const [x, setX] = useState({ kind: 'advance', amount: '', notes: '', date: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Cairo' }) });
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  async function save() {
    setBusy(true);
    try { onSaved(await call('POST', `/api/finance/engineers/${user.id}/ledger`, x)); toast('اتسجل'); onClose(); }
    catch (e) { toast(e.message, 'error'); } finally { setBusy(false); }
  }
  return (
    <Modal title={`حركة جديدة — ${user.username}`} onClose={onClose} footer={<>
      <button className="btn btn-ghost btn-sm" onClick={onClose}>إلغاء</button>
      <button className="btn btn-primary btn-sm" onClick={save} disabled={busy || !Number(x.amount)}>{busy ? '...' : 'حفظ'}</button></>}>
      <div dir="rtl">
        {Object.entries(KINDS).map(([k, [l, d]]) => (
          <label key={k} className="flex gap-8" style={{ padding: '7px 8px', borderRadius: 8, cursor: 'pointer', background: x.kind === k ? 'var(--primary-dim)' : 'transparent' }}>
            <input type="radio" checked={x.kind === k} onChange={() => setX(v => ({ ...v, kind: k }))} />
            <div><div className="fw-600 text-sm">{l}</div><div className="text-xs text-dim">{d}</div></div>
          </label>
        ))}
        <div className="grid-2 mt-8">
          <div className="field"><label>المبلغ</label><input className="input" type="number" dir="ltr" value={x.amount} onChange={e => setX(v => ({ ...v, amount: e.target.value }))} autoFocus /></div>
          <div className="field"><label>التاريخ</label><input className="input" type="date" value={x.date} onChange={e => setX(v => ({ ...v, date: e.target.value }))} /></div>
        </div>
        <div className="field"><label>ملاحظة</label><input className="input" value={x.notes} onChange={e => setX(v => ({ ...v, notes: e.target.value }))} placeholder="مثلاً: عهدة شراء موتور كرايو" /></div>
      </div>
    </Modal>
  );
}

export function EngineerProfile() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [modal, setModal] = useState(false);
  const [del, setDel] = useState(null);
  const [tab, setTab] = useState('ledger');
  const toast = useToast();
  const role = useRole();
  const isAdmin = role === 'admin';
  const load = useCallback(() => { call('GET', `/api/finance/engineers/${id}`).then(setA).catch(e => toast(e.message, 'error')); }, [id, toast]);
  useEffect(() => { load(); }, [load]);
  if (!a) return <div className="page"><div className="empty">...</div></div>;
  const t = a.totals;

  return (
    <div className="page" dir="rtl">
      {role !== 'technician' && <div className="text-sm mb-16"><Link to="/engineers">→ المهندسين</Link></div>}
      <div className="card mb-16" style={{ padding: 20 }}>
        <div className="flex items-center gap-12" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div><div className="fw-600" style={{ fontSize: 22 }}>👷 {a.user.username}</div><div className="text-sm text-dim">{a.user.role} · {t.tickets} تيكت</div></div>
          <div style={{ textAlign: 'center' }}><div className="text-xs text-dim">الرصيد</div><Balance value={a.balance} big /></div>
          {isAdmin && <button className="btn btn-primary" onClick={() => setModal(true)}>+ حركة (عهدة / صرف / رد)</button>}
        </div>
        <div className="text-xs text-dim mt-8">الرصيد = المصاريف اللي دفعها + العمولات المستحقة + رد العهد − العهد − اللي اتصرفله.  «له» يعني الشركة مديونة له، «عليه» يعني معاه فلوس للشركة.</div>
      </div>
      <div className="stats-grid mb-16">
        <div className="stat-card"><div className="stat-label">عمولات مستحقة</div><div className="stat-value green mono">{money(t.commissions)}</div></div>
        <div className="stat-card"><div className="stat-label">مصاريف دفعها</div><div className="stat-value blue mono">{money(t.expenses_paid)}</div></div>
        <div className="stat-card"><div className="stat-label">عهد استلمها</div><div className="stat-value red mono">{money(t.advances)}</div></div>
        <div className="stat-card"><div className="stat-label">اتصرفله</div><div className="stat-value yellow mono">{money(t.payouts)}</div></div>
        {t.pending_commission > 0 && <div className="stat-card"><div className="stat-label">⏳ عمولة مستنية الدفع</div><div className="stat-value mono">{money(t.pending_commission)}</div></div>}
      </div>

      <div className="flex gap-8 mb-16">
        <button className={`btn btn-sm ${tab === 'ledger' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('ledger')}>كشف الحساب</button>
        <button className={`btn btn-sm ${tab === 'tickets' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('tickets')}>العمولات بالتيكت ({a.commissions.length})</button>
      </div>

      {tab === 'ledger' ? (
        <div className="table-wrap">
          <table>
            <thead><tr><th>التاريخ</th><th>الحركة</th><th>البيان</th><th>المبلغ</th><th>الرصيد بعدها</th><th></th></tr></thead>
            <tbody>
              {a.entries.length === 0 ? <tr><td colSpan={6}><div className="empty">مفيش حركات</div></td></tr> : a.entries.map(e => {
                const [ic, l, c] = TYPE[e.type] || ['', e.type, 'badge-gray'];
                return (
                  <tr key={e.id}>
                    <td className="mono text-sm">{e.date}</td>
                    <td><span className={`badge ${c}`}>{ic} {l}</span></td>
                    <td className="text-sm">{e.ticket_id ? <Link to={`/tickets/${e.ticket_id}`}>{e.title}</Link> : e.title}
                      {e.sub && <div className="text-xs text-dim">{e.sub}</div>}</td>
                    <td className="mono fw-600" dir="ltr" style={{ textAlign: 'right', color: e.amount >= 0 ? 'var(--green)' : 'var(--red)' }}>{e.amount >= 0 ? '+' : '−'}{money(Math.abs(e.amount))}</td>
                    <td><Balance value={e.balance} /></td>
                    <td>{isAdmin && e.ledger_id && <button className="btn btn-danger btn-sm" onClick={() => setDel(e.ledger_id)}>✕</button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>التيكت</th><th>دوره</th><th>الدخل</th><th>المصاريف</th><th>الصافي</th><th>عمولته</th><th>الحالة</th></tr></thead>
            <tbody>
              {a.commissions.length === 0 ? <tr><td colSpan={7}><div className="empty">مفيش تيكتات متسجل عليها</div></td></tr> : a.commissions.map(c => {
                const [l, cl] = COM_STATUS[c.status];
                return (
                  <tr key={c.ticket_id}>
                    <td><Link to={`/tickets/${c.ticket_id}`} className="fw-600">{c.device_name || c.short_id}</Link><div className="text-xs text-dim">{c.client_name} · <span className="mono">{c.serial}</span></div></td>
                    <td className="text-sm">{c.role === 'main' ? 'أساسي' : 'مساعد'}{c.partner && ' (مع زميل)'}</td>
                    <td className="mono">{money(c.income)}</td>
                    <td className="mono">{money(c.expenses)}</td>
                    <td className="mono">{money(c.profit)}</td>
                    <td className="mono fw-600">{money(c.amount)}</td>
                    <td><span className={`badge ${cl}`}>{l}</span>{c.earned_at && <div className="text-xs text-dim mono">{c.earned_at}</div>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {modal && <EntryModal user={a.user} onClose={() => setModal(false)} onSaved={setA} />}
      {del && <ConfirmModal message="تمسح الحركة دي؟" onConfirm={() => { call('DELETE', `/api/finance/ledger/${del}`).then(setA).catch(e => toast(e.message, 'error')); setDel(null); }} onClose={() => setDel(null)} />}
    </div>
  );
}
